import { useEffect, useRef } from 'react'
import { useMidi } from './hooks/useMidi'
import { useAudio } from './hooks/useAudio'
import { useTuningStore } from './store/tuningStore'
import { Sidebar } from './components/Sidebar'
import { Keyboard } from './components/Keyboard'
import { TuningPanel } from './components/TuningPanel'
import { TopBar } from './components/TopBar'

function App() {
  const { checkSupport } = useMidi()
  const { resumeContext, playNoteWithTuning, preloadSamples } = useAudio()
  const { activeNotes } = useTuningStore()
  const previousActiveNotesRef = useRef<Set<number>>(new Set())

  useEffect(() => {
    checkSupport()
  }, [checkSupport])

  // Preload samples early (don't wait for user interaction)
  useEffect(() => {
    preloadSamples().catch(console.error)
  }, [preloadSamples])

  // Play notes when MIDI notes become active
  useEffect(() => {
    activeNotes.forEach((midiNote) => {
      if (!previousActiveNotesRef.current.has(midiNote)) {
        // New note activated
        console.log(`🎵 Playing MIDI note: ${midiNote}`)
        playNoteWithTuning(midiNote).catch((error) => {
          console.error(`❌ Error playing note ${midiNote}:`, error)
        })
      }
    })
    
    // Stop notes that are no longer active
    previousActiveNotesRef.current.forEach((midiNote) => {
      if (!activeNotes.has(midiNote)) {
        console.log(`🔇 Note released: ${midiNote}`)
      }
    })
    
    previousActiveNotesRef.current = new Set(activeNotes)
  }, [activeNotes, playNoteWithTuning])

  // Resume audio context on first user interaction and preload samples
  useEffect(() => {
    const handleFirstInteraction = async () => {
      await resumeContext()
      // Preload samples after audio context is resumed
      preloadSamples()
      document.removeEventListener('click', handleFirstInteraction)
      document.removeEventListener('keydown', handleFirstInteraction)
    }

    document.addEventListener('click', handleFirstInteraction)
    document.addEventListener('keydown', handleFirstInteraction)

    return () => {
      document.removeEventListener('click', handleFirstInteraction)
      document.removeEventListener('keydown', handleFirstInteraction)
    }
  }, [resumeContext, preloadSamples])

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            <Keyboard />
            <TuningPanel />
          </div>
        </div>
      </div>
    </div>
  )
}

export default App
